import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import uuid from 'short-unique-id';
import {
    AdvancedFilterFieldTypesEnum, AdvanceFilterParamType, IAdvanceFilter
} from '../../../shared/advanced-filters/components/advance-filter';
import { IColumnSettings } from '../../../shared/shared/table/components/custom-columns-settings/column-settings';

import { ITablePagerOptions } from '../../../shared/shared/table/components/table-pager/ITablePagerOptions';
import { IExportOptions } from '../../contract-control/modals/export-csv-modal/export-csv-modal.component';
import { IMetadataListDTO, MetaDataTypeEnum } from '../../settings/documentfields/metadata-item.dto';
import { IPageableRequest } from '../../users/userslisting/dto/get-user-list.dto';
import { HttpActionState } from '../../users/userslisting/store/users-listing.reducers';
import { IUpdatePreviewColumnFormData } from '../documents.service';
import { DocumentTypeCustomBoxesDaum, IContractData } from '../dto/contract-data.dto';
import { ContractControlMultiSection, DocumentDetailsStatus, DocumentInternalReminder, IDetailFieldValuesDTO, IDocumentDetailRootDTO, Tag } from '../dto/document-details.dto';
import { IDocumentDto, IDocumentFieldsData, IDocumentTypesDto, IUserGroupFilter, IUserGroupFilterDto } from '../dto/document-listing.dto';
import { DocumentStatusesEnum } from '../dto/document-statuses.enum';
import { FilterDocumentStatusesEnum } from '../dto/filter-document-statuses.enum';
import { IGetDocumentsDto } from '../dto/get-documents-dto';
import * as DocumentListingActions from './document-listing.actions';
import { IPlaybooksItemDTO } from '../../legal-hub/dto/playbooks-listening.dto';
import { IGuideLineListFilter } from '../../legal-hub/playbooks/playbook-list/playbook-list.component';
import { IUpdateFieldsData } from './document-listing.facade';
import { DocumentDetailActions } from './document-listing.actions';
import { FieldData } from '../../edit-template/edit-template.dto';
import { ICategory } from 'app/main/shared/shared/table/components/category-name/category-name.component';

export interface IDocumentFilterState {
    status?: FilterDocumentStatusesEnum | null
    dateFrom?: string | null,
    dateTo?: string | null,
    documentTypeId?: number | null,
    categoryId?: number | null,
    userFilter?: IUserGroupFilter,
    perPageRecords?: number | null
}

export enum DocumentViewVariantEnum {
    table_view = 'table_view',
    chart_view = 'chart_view',
    graph_view = 'graph_view',
    folder_view = 'folder_view',
    trash_view = 'trash_view',
}

export interface IDocumentListingState {
    viewVariant: DocumentViewVariantEnum,
    selection: IDocumentDto[],
    requestState: HttpActionState,
    documents: EntityState<IDocumentDto>,
    documentTypes: EntityState<IDocumentTypesDto>,
    documentTypesRequestState: HttpActionState,
    users: {
        users: IUserGroupFilterDto[],
        groups: IUserGroupFilterDto[]
    }
    usersRequestState: HttpActionState,
    paging?: ITablePagerOptions;
    search?: string;
    lastSelectedDocumentId?: number,
    filter: IDocumentFilterState,
    documentsDeletesRequestState: HttpActionState,
    documentsRestoreRequestState: HttpActionState,
    tableSettings: ITableSettings,
    fetchCustomColumnsRequestState: HttpActionState,
    updateCustomColumnsRequestState: HttpActionState,
    documentLoading: boolean,
    documentData: IDocumentDetailRootDTO,
    documentDataRequestState: HttpActionState,
    documentIndexData: IDocumentDetailRootDTO,
    documentIndexDataRequestState: HttpActionState,
    updateFieldsDataRequestState: HttpActionState,
    contractData: IContractData;
    updateDocumentNameRequestState: HttpActionState
    advancedFilterRequestState: HttpActionState;
    metadata?: IMetadataListDTO;
    advancedFilterSearch?: string;
    advancedFilterPage?: number;
    appliedAdvancedFilters: IAdvanceFilter<AdvanceFilterParamType>[],
    categoriesByDocId?: number[],
    ownerFilterSearch?: string,
    updateDocumentPreviewColumnsRequestState?: HttpActionState,
    resetDocumentPreviewColumnsRequestState: HttpActionState,
    legalHub: {
        selectedPlaybook: IPlaybooksItemDTO,
        filters: IGuideLineListFilter
    };
    rightBarHigherZindex: boolean;

}


export interface ITableSettings {
    column_settings: EntityState<IColumnSettings>,
    order_settings: EntityState<IColumnSettings>
}

export interface IAdvancedSearchRequest extends IExportDocumentsRequest {
    advanced?: AdvanceFilterParamType[]
}

export const documentListingAdapter: EntityAdapter<IDocumentDto> = createEntityAdapter<IDocumentDto>({
    selectId: model => model.id,
});

export const documentsTypeAdapter: EntityAdapter<IDocumentTypesDto> = createEntityAdapter<IDocumentTypesDto>({
    selectId: model => model.id,
});

export const documentsUserAdapter: EntityAdapter<IUserGroupFilterDto> = createEntityAdapter<IUserGroupFilterDto>({
    selectId: model => model.id,
});

export const columnSettingsAdapter: EntityAdapter<IColumnSettings> = createEntityAdapter<IColumnSettings>({
    selectId: model => model.uuid,
});

export interface IDocumentExportPreview {
}

export interface IDocumentPreviewRequest extends IPageableRequest {
    ignored_contracts: number[],
}

export interface IExportDocumentsRequest extends IExportOptions<IGetDocumentsDto>, Partial<IGetDocumentsDto> {
    ignored_contracts?: number[],
    format?: 'csv' | 'xlsx',
}


const uuidGenerator = new uuid({ length: 6, dictionary: 'number' });

const initialState: IDocumentListingState = {
    viewVariant: DocumentViewVariantEnum.table_view,
    selection: [],
    requestState: HttpActionState.Init,
    documents: documentListingAdapter.getInitialState(),
    documentTypes: documentsTypeAdapter.getInitialState(),
    documentTypesRequestState: HttpActionState.Init,
    users: {
        users: [],
        groups: []
    },
    usersRequestState: HttpActionState.Init,
    paging: {
        pageSize: 1,
        currentPage: 1,
        pagesCount: 1,
        total: 0
    },
    search: '',
    filter: {
        status: null,
        dateTo: null,
        dateFrom: null,
        documentTypeId: null,
        userFilter: null,
        perPageRecords: null
    },
    documentData: null,
    documentLoading: true,
    documentDataRequestState: HttpActionState.Init,
    documentIndexData: null,
    documentIndexDataRequestState: HttpActionState.Init,
    updateFieldsDataRequestState: HttpActionState.Init,
    lastSelectedDocumentId: null,
    documentsDeletesRequestState: HttpActionState.Init,
    documentsRestoreRequestState: HttpActionState.Init,
    tableSettings: {
        column_settings: columnSettingsAdapter.getInitialState(),
        order_settings: columnSettingsAdapter.getInitialState()
    },
    fetchCustomColumnsRequestState: HttpActionState.Init,
    updateCustomColumnsRequestState: HttpActionState.Init,
    resetDocumentPreviewColumnsRequestState: HttpActionState.Init,
    contractData: null,
    updateDocumentNameRequestState: HttpActionState.Init,
    advancedFilterRequestState: HttpActionState.Init,
    metadata: null,
    advancedFilterSearch: '',
    advancedFilterPage: 1,
    appliedAdvancedFilters: [],
    categoriesByDocId: null,
    ownerFilterSearch: null,
    legalHub: null,
    rightBarHigherZindex: false,

}


export const documentListingReducers = createReducer(
    initialState,
    on(DocumentListingActions.resetState, () => ({
        ...initialState
    })),
    on(DocumentListingActions.fetchDocuments, (state) => ({
        ...state,
        requestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.fetchDocumentsSucceeded, (state, { documentsList }) => ({
        ...state,
        paging: {
            currentPage: documentsList.current_page || 1,
            pagesCount: documentsList.last_page || 1,
            pageSize: documentsList.per_page || 1,
            total: documentsList.total || 0
        },
        requestState: HttpActionState.Done,
        documents: documentListingAdapter.setAll(documentsList.data, state.documents),
        selection: initialState.selection
    })),
    on(DocumentListingActions.fetchDocumentsFailed, (state) => ({
        ...state,
        requestState: HttpActionState.Failed,
        paging: initialState.paging,
        documents: initialState.documents,
        lastSelectedDocumentId: initialState.lastSelectedDocumentId,
    })),
    on(DocumentListingActions.searchChange, (state, { searchValue }) => ({
        ...state,
        search: searchValue
    })),
    on(DocumentListingActions.changePage, (state, { pageNumber }) => ({
        ...state,
        paging: {
            ...state.paging,
            currentPage: pageNumber
        }
    })),
    on(DocumentListingActions.documentClicked, (state, { documentId }) => ({
        ...state,
        lastSelectedDocumentId: documentId
    })),


    on(DocumentListingActions.fetchDocumentData, (state) => {
        return {
            ...state,
            documentLoading: true,
            documentData: null,
            documentDataRequestState: HttpActionState.Busy
        }
    }),


    on(DocumentListingActions.fetchDocumentDataSucceeded, (state, { document }) => {
        return {
            ...state,
            documentLoading: false,
            documentData: document,
            documentDataRequestState: HttpActionState.Done

        }
    }),

    on(DocumentListingActions.fetchDocumentDataFailed, (state) => {
        return {
            ...state,
            documentLoading: false,
            documentData: null,
            documentDataRequestState: HttpActionState.Failed

        }
    }),

    on(DocumentListingActions.documentIndexDataCall, (state) => {
        return {
            ...state,
            documentIndexDataRequestState: HttpActionState.Busy,
        }
    }),

    on(DocumentListingActions.documentIndexSucceeded, (state, { indexData }) => {
        return {
            ...state,
            documentIndexDataRequestState: HttpActionState.Done,
            documentIndexData: {
                ...indexData as unknown as IDocumentDetailRootDTO
            }
        }
    }),

    on(DocumentListingActions.documentIndexFailed, (state) => {
        return {
            ...state,
            documentIndexDataRequestState: HttpActionState.Failed
        }
    }),

    on(DocumentListingActions.makeDocumentRevokedSucceeeded, (state, { documentId, status }) => {
        return {
            ...state,
            documents: documentListingAdapter.updateOne(
                {
                    id: documentId,
                    changes: {
                        status: status
                    }
                },
                state.documents),
            documentData: (state.documentData?.template?.id === documentId) ? {
                ...state.documentData,
                template: {
                    ...state?.documentData?.template,
                    status: status
                }
            } : state.documentData
        }
    }),

    on(DocumentListingActions.updateFieldsData, (state) => ({
        ...state,
        updateFieldsDataRequestState: HttpActionState.Busy,
    })),

    on(DocumentListingActions.updateFieldsDataSuccess, (state, { payload }) => {
         const templateId = state?.documentData?.template?.id ?? null;

        // Build new fields data regardless, but only update documents adapter if we have an id
        const currentFieldsData = templateId ? (state.documents.entities[templateId]?.fields_datas ?? {}) : (state?.documentData?.template_fields_datas ?? {});
        const newFieldsData: IDocumentFieldsData = {
            ...currentFieldsData as IDocumentFieldsData,
            data: payload.data as FieldData[]
        };

        // If there is no templateId, just update documentData.template_fields_datas and return
        if (!templateId) {
            return {
                ...state,
                updateFieldsDataRequestState: HttpActionState.Done,
                documentData: {
                    ...state?.documentData,
                    template_fields_datas: newFieldsData
                }
            };
        }
        return {
            ...state,
            updateFieldsDataRequestState: HttpActionState.Done,
            documentData: {
                ...state?.documentData,
                template_fields_datas: newFieldsData
            },
            documents: documentListingAdapter.updateOne(
                {
                    id: state?.documentData.template.id,
                    changes: {
                        fields_datas: newFieldsData
                    }
                },
                state.documents),
        }
    }),


    on(DocumentListingActions.updateFieldsDataFailure, (state, { payload }) => {
        const currentFieldsData = state.documents.entities[state?.documentData?.template?.id]?.fields_datas;
        const newFieldsData: IDocumentFieldsData = {
            ...currentFieldsData,
            data: payload.data
        };
        return {
            ...state,
            updateFieldsDataRequestState: HttpActionState.Done,
            documentData: {
                ...state?.documentData,
                template_fields_datas: newFieldsData
            }
        }
    }),

    on(DocumentListingActions.updateFieldsDataFailure, (state) => ({
        ...state,
        updateFieldsDataRequestState: HttpActionState.Failed
    })),

    on(DocumentListingActions.updateDocumentStatusSucceeeded, (state, { documentId, status }) => ({
        ...state,
        documents: documentListingAdapter.updateOne(
            {
                id: documentId,
                changes: {
                    status: status,
                    status_updated_manually: 1
                }
            },
            state.documents),
        documentData: (state.documentData?.template?.id === documentId) ? {
            ...state.documentData,
            template: {
                ...state?.documentData?.template,
                status: status
            }
        } : state.documentData
    })),

    on(DocumentListingActions.updateDocumentStatusFromPusher, (state, { documentId, status, updated_at }) => ({
        ...state,
        documents: documentListingAdapter.updateOne(
            {
                id: documentId,
                changes: {
                    status: status,
                    updated_at: updated_at,
                    status_updated_at: updated_at
                }
            },
            state.documents),
        documentData: (state.documentData?.template?.id === documentId)
            ? {
                ...state.documentData,
                template: {
                    ...state?.documentData?.template,
                    status: status,
                    updated_at: updated_at
                }
            }
            : state.documentData
    })),

    on(DocumentListingActions.updateDocumentTypeSucceeeded, (state, { documentType }) => {
        return {
            ...state,
            documentData: {
                ...state?.documentData,
                template_details: {
                    ...state.documentData?.template_details,
                    document_type_id: documentType.id,
                }
            }
        }
    }),

    on(DocumentListingActions.updateDocumentData, (state, { document }) => ({
        ...state,
        documents: documentListingAdapter.upsertOne(document, state.documents)
    }
    )),

    on(DocumentListingActions.updateDocumentDueDateSucceeeded, (state, { due_date, status }) => ({
        ...state,
        documents: documentListingAdapter.updateOne(
            {
                id: state.documentData?.template?.id,
                changes: {
                    status: (status as DocumentStatusesEnum)
                }
            },
            state.documents),
        documentData: {
            ...state?.documentData,
            template_details: {
                ...state?.documentData?.template_details,
                due_date: due_date
            },
            template: {
                ...state?.documentData?.template,
                status: (status as DocumentDetailsStatus)
            }
        }
    })),

    on(DocumentListingActions.updateTagsInDocumentSucceeeded, (state, { tag }) => ({
        ...state,
        documentData: {
            ...state?.documentData,
            tags: state?.documentData?.tags && tag ? [...state?.documentData?.tags, tag] : []
        }
    })),

    on(DocumentListingActions.removeTagFromDocumentSucceeeded, (state, { tag }) => ({
        ...state,
        documentData: {
            ...state?.documentData,
            tags: removeTag(state.documentData?.tags, tag)
        }
    })),


    on(DocumentListingActions.updateDocumentVisibilitySucceeeded, (state, { visibility }) => ({
        ...state,
        documentData: {
            ...state?.documentData,
            template_details: {
                ...state?.documentData?.template_details,
                visibility: visibility
            }
        }
    })),


    /*Filters*/
    on(DocumentListingActions.fetchDocumentTypes, (state) => ({
        ...state,
        documentTypesRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.fetchDocumentTypesSucceeded, (state, { types }) => ({
        ...state,
        documentTypesRequestState: HttpActionState.Done,
        documentTypes: documentsTypeAdapter.setAll(types, state?.documentTypes)
    })),
    on(DocumentListingActions.fetchDocumentTypesFailed, (state) => ({
        ...state,
        documentTypesRequestState: HttpActionState.Done,
        documentTypes: documentsTypeAdapter.getInitialState()
    })),
    on(DocumentListingActions.setDocumentType, (state, { documentType }) => ({
        ...state,
        documentTypesRequestState: HttpActionState.Done,
        documentTypes: documentsTypeAdapter.setOne(documentType, state?.documentTypes)
    })),


    on(DocumentListingActions.fetchUserGroups, (state) => ({
        ...state,
        usersRequestState: HttpActionState.Busy
    })),

    on(DocumentListingActions.fetchUsersSucceeded, (state, { users }) => ({
        ...state,
        usersRequestState: HttpActionState.Done,
        users: users
    })),
    on(DocumentListingActions.fetchUsersFailed, (state) => ({
        ...state,
        usersRequestState: HttpActionState.Failed,
        users: initialState.users
    })),

    on(DocumentListingActions.setPerPageRecords, (state, { perPageRecords }) => ({
        ...state,
        filter: {
            ...state.filter,
            perPageRecords: perPageRecords
        },
        paging: {
            ...state.paging,
            currentPage: 1
        }
    })),

    on(DocumentListingActions.filterByStatus, (state, { status }) => ({
        ...state,
        filter: {
            ...state.filter,
            status: status
        },
        paging: {
            ...state.paging,
            currentPage: 1
        }
    })),
    on(DocumentListingActions.filterByFromDate, (state, { date }) => ({
        ...state,
        filter: {
            ...state.filter,
            dateFrom: date
        },
        paging: {
            ...state.paging,
            currentPage: 1
        }
    })),
    on(DocumentListingActions.filterByToDate, (state, { date }) => ({
        ...state,
        filter: {
            ...state.filter,
            dateTo: date
        },
        paging: {
            ...state.paging,
            currentPage: 1
        }
    })),
    on(DocumentListingActions.filterByDocumentType, (state, { id }) => ({
        ...state,
        filter: {
            ...state.filter,
            documentTypeId: id
        },
        paging: {
            ...state.paging,
            currentPage: 1
        }
    })),
    on(DocumentListingActions.filterByUser, (state, { id }) => ({
        ...state,
        filter: {
            ...state.filter,
            userFilter: id
        }
    })),

    on(DocumentListingActions.filterByCategory, (state, { id }) => ({
        ...state,
        filter: {
            ...state.filter,
            categoryId: id
        }
    })),

    on(DocumentListingActions.setBasicDocumentFilters, (state, filters) => {
        const basicFilters = { ...filters.filters };
        delete basicFilters.search;

        return ({
            ...state,
            filter: basicFilters,
            search: filters.filters.search
        })
    }),
    on(DocumentListingActions.changeActiveView, (state, { view }) => ({
        ...state,
        viewVariant: view,
        selection: initialState.selection
    })),
    on(DocumentListingActions.documentsSelected, (state, { documents }) => ({
        ...state,
        selection: documents
    })),

    on(DocumentListingActions.deleteSelectedDocuments, (state) => ({
        ...state,
        documentsDeletesRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.deleteSelectedDocumentsSucceeded, (state) => ({
        ...state,
        documentsDeletesRequestState: HttpActionState.Done
    })),
    on(DocumentListingActions.deleteSelectedDocumentsFailed, (state) => ({
        ...state,
        documentsDeletesRequestState: HttpActionState.Failed
    })),


    on(DocumentListingActions.restoreSelectedDocuments, (state) => ({
        ...state,
        documentsRestoreRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.restoreSelectedDocumentsSucceeded, (state) => ({
        ...state,
        documentsRestoreRequestState: HttpActionState.Done
    })),
    on(DocumentListingActions.restoreSelectedDocumentsFailed, (state) => ({
        ...state,
        documentsRestoreRequestState: HttpActionState.Failed
    })),

    /*Table settings*/
    on(DocumentListingActions.fetchCustomColumns, (state) => ({
        ...state,
        fetchCustomColumnsRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.fetchCustomColumnsSucceeded, (state, { columns }) => {
        return {
            ...state,
            fetchCustomColumnsRequestState: HttpActionState.Done,
            tableSettings: {
                ...state.tableSettings,
                column_settings: columnSettingsAdapter.setAll(columns.column_settings, state.tableSettings.column_settings),
                order_settings: columnSettingsAdapter.setAll(columns.column_settings, state.tableSettings.order_settings)
            }
        }
    }),
    on(DocumentListingActions.fetchCustomColumnsFailed, (state) => ({
        ...state,
        fetchCustomColumnsRequestState: HttpActionState.Failed
    })),


    on(DocumentListingActions.updateCustomColumns, (state, { skipSkeleton }) => ({
        ...state,
        fetchCustomColumnsRequestState: skipSkeleton ? HttpActionState.Busy : HttpActionState.Done
    })),
    on(DocumentListingActions.updateCustomColumnsSucceeded, (state, { column_settings }) => ({
        ...state,
        fetchCustomColumnsRequestState: HttpActionState.Done,
        tableSettings: {
            ...state.tableSettings,
            column_settings: columnSettingsAdapter.setAll(column_settings, state.tableSettings.column_settings),
            order_settings: columnSettingsAdapter.setAll(column_settings, state.tableSettings.column_settings)
        }
    })),
    on(DocumentListingActions.updateCustomColumnsFailed, (state) => ({
        ...state,
        fetchCustomColumnsRequestState: HttpActionState.Failed
    })),


    /** Right sidebar */

    on(DocumentListingActions.setCustomBoxes, (state, { customBoxes }) => {
        return {
            ...state,
            contractData: {
                ...state.contractData,
                customBoxes: customBoxes as unknown as DocumentTypeCustomBoxesDaum[]
            }
        }
    }),

    on(DocumentListingActions.connectContractSuccess, (state, { connectedContract }) => {
        return {
            ...state,
            documentData: {
                ...state.documentData,
                contract_control_multi_sections: connectedContract as unknown as ContractControlMultiSection[]
            }
        }
    }),


    on(DocumentListingActions.updateContractForm, (state, { key, data }) => {
        return {
            ...state,
            contractData: {
                ...state.contractData,
                contractForm: {
                    ...(state.contractData)?.contractForm,
                    [key]: data
                }
            }
        };
    }),

    on(DocumentListingActions.setContractData, (state, { key, contractData }) => {
        return {
            ...state,
            contractData: {
                ...state.contractData,
                [key]: contractData
            }
        }
    }),

    on(DocumentListingActions.updateFormStatus, (state, { status }) => ({
        ...state,
        contractData: {
            ...state.contractData,
            currentFormStatus: status
        }
    })),

    on(DocumentListingActions.updateFormStep, (state, { step }) => ({
        ...state,
        contractData: {
            ...state.contractData,
            step: step
        }
    })),

    on(DocumentListingActions.updateFormSubmittion, (state, { submitted }) => ({
        ...state,
        contractData: {
            ...state.contractData,
            isCurrentFormSubmitted: submitted
        }
    })),

    on(DocumentListingActions.updateDocumentName, (state) => ({
        ...state,
        updateDocumentNameRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.fetchAdvancedFilters, (state) => ({
        ...state,
        advancedFilterRequestState: HttpActionState.Busy
    })),
    on(DocumentListingActions.fetchAdvancedFiltersSuccess, (state, { metadata }) => ({
        ...state,
        advancedFilterRequestState: HttpActionState.Done,
        metadata: metadata
    })),
    on(DocumentListingActions.fetchAdvancedFiltersFailure, (state) => ({
        ...state,
        advancedFilterRequestState: HttpActionState.Failed
    })),
    on(DocumentListingActions.advancedFilterSearch, (state, { searchString }) => ({
        ...state,
        advancedFilterSearch: searchString
    })),
    on(DocumentListingActions.addAdvancedFilter, (state, { filter }) => ({
        ...state,
        appliedAdvancedFilters: addAdvanceFilter(filter, state.appliedAdvancedFilters)
    })),
    on(DocumentListingActions.updateAdvancedFilter, (state, { filter }) => ({
        ...state,
        appliedAdvancedFilters: [...updateAdvanceFilter(filter, state.appliedAdvancedFilters)]
    })),
    on(DocumentListingActions.removeAdvancedFilter, (state, { filter }) => ({
        ...state,
        appliedAdvancedFilters: removeAdvanceFilter(filter, state.appliedAdvancedFilters)
    })),

    on(DocumentListingActions.setAdvancedDocumentFilters, (state, appliedAdvancedFilters) => ({
        ...state,
        appliedAdvancedFilters: appliedAdvancedFilters.filters
    })),

    on(DocumentListingActions.onFilterClose, (state, { filter }) => ({
        ...state,
        appliedAdvancedFilters: onFilterClose(filter, state.appliedAdvancedFilters)
    })),

    on(DocumentListingActions.updateDocumentColumnValue, (state) => ({
        ...state,
        updateDocumentPreviewColumnsRequestState: HttpActionState.Busy
    })),

    on(DocumentListingActions.updateDocumentColumnValueSuccess, (state, { formData }) => ({
        ...state,
        updateDocumentPreviewColumnsRequestState: HttpActionState.Done,
        documentData: updatePreviewColumnValue(state.documentData, formData)
    })),

    on(DocumentListingActions.updateDocumentColumnValueFailure, (state) => ({
        ...state,
        updateDocumentPreviewColumnsRequestState: HttpActionState.Failed
    })),

    on(DocumentListingActions.updateDocumentNameSuccess, (state, { documentId, name }) => ({
        ...state,
        documents: documentListingAdapter.updateOne(
            {
                id: documentId,
                changes: {
                    name: name,
                    visible_name: name,
                }
            },
            state.documents),
        documentData: {
            ...state.documentData,
            template: {
                ...state.documentData.template,
                name: name
            },
            template_details: {
                ...state.documentData.template_details,
                visible_name: name
            }
        },
        updateDocumentNameRequestState: HttpActionState.Done
    })),


    on(DocumentListingActions.updateDocumentNameFailure, (state) => ({
        ...state,
        updateDocumentNameRequestState: HttpActionState.Failed
    })),

    on(DocumentListingActions.filterCategoriesByDocType, (state, { categories }) => ({
        ...state,
        categoriesByDocId: categories == null || categories?.length === 0
            ? null
            : categories
    })),

    on(DocumentListingActions.ownerSearchChanged, (state, { search }) => ({
        ...state,
        ownerFilterSearch: search
    })),

    on(DocumentListingActions.updateInternalReminder, (state, { reminders }) => {
        const isRemindersExpired = areAllInternalRemindersPassed(reminders);
        return {
            ...state,
            documentData: {
                ...state.documentData,
                document_internal_reminder: reminders,
                is_reminders_expire: isRemindersExpired
            },
            documents: documentListingAdapter.updateOne(
                {
                    id: state?.documentData?.template?.id,
                    changes: {
                        document_internal_reminder: reminders,
                        is_reminders_expire: isRemindersExpired
                    }
                },
                state.documents
            )
        };
    }),
    /* LEGAL HUB */
    on(DocumentListingActions.setLegalHubFilters, (state, { filters }) => ({
        ...state,
        legalHub: {
            ...state.legalHub,
            filters: filters
        }
    })),
    on(DocumentListingActions.selectLegalHubPlaybook, (state, { playbook }) => ({
        ...state,
        legalHub: {
            ...state.legalHub,
            selectedPlaybook: playbook
        }
    })),
    
    on(DocumentListingActions.rightBarHigherZindex, (state, { higher }) => ({
        ...state,
        rightBarHigherZindex: !!higher
    })),
    on(DocumentListingActions.updateSpecialActions, (state, { action }) => ({
       ...state,
        documentData: {
            ...state.documentData,
            document_special_action: action
        }
    })),



    on(DocumentDetailActions.changeDocumentTypeSuccess, (state, { groups }) => {
        return {
        ...state,
        documentData: {
            ...state.documentData,
            layout_settings: {
                ...state.documentData?.layout_settings,
                groups: groups
            }
        }
    }}),
    on(DocumentDetailActions.updateDocumentEntityDataSuccess, (state, { settings, categories }) => ({
        ...state,
        documentData: {
            ...state.documentData,
            layout_settings: {
                ...state.documentData?.layout_settings,
                ...(settings || {})
            }
        },
        documents: documentListingAdapter.updateOne(
            {
id: state.documentData?.template?.id,
                changes: {
                    categories: categories,
                }
            },
            state.documents
        )

    })),

    on(DocumentDetailActions.changeDocumentTypeFailure, (state) => ({
        ...state,
        changeDocumentTypeRequestState: HttpActionState.Failed
    }))
);



/**
 * Updates the preview column value of a selected document based on the provided form data.
 *
 * @param selectedDocument - The document whose preview column value needs to be updated.
 * @param formData - The form data containing the new value and associated metadata.
 * @returns A new instance of the document with the updated preview column value.
 *
 * The function performs the following:
 * - If the `template_fields_datas` property of the document contains the specified `uuid` from the form data,
 *   it updates the corresponding field's `value` and `old_value`.
 * - If the `template_fields_datas` does not contain the specified `uuid`, it updates the `detailFieldValues` array:
 *   - If a matching `metadata_id` is found, it updates the `value` and `old_value` of the corresponding field.
 *   - If no matching `metadata_id` is found, it adds a new entry to the `detailFieldValues` array with the provided data.
 */
function updatePreviewColumnValue(selectedDocument: IDocumentDetailRootDTO, formData: IUpdatePreviewColumnFormData): IDocumentDetailRootDTO {
    if (selectedDocument.template_fields_datas?.data[formData.uuid]) {
        return {
            ...selectedDocument,
            template_fields_datas: {
                ...selectedDocument.template_fields_datas,
                data: {
                    ...selectedDocument.template_fields_datas.data,
                    [formData.uuid]: {
                        ...selectedDocument.template_fields_datas.data[formData.uuid],
                        old_value: selectedDocument.template_fields_datas.data[formData.uuid].value,
                        value: formData.value,
                    }
                }
            }
        }
    }
    return {
        ...selectedDocument,
        detailFieldValues: selectedDocument.detailFieldValues.length ? selectedDocument.detailFieldValues?.map((x) => {
            if (x.metadata_id == Number(formData.selected_field_type_id)) {
                return {
                    ...x,
                    old_value: formData.meta_data?.meta_data_type === MetaDataTypeEnum.checkbox ? Number(x.value) : String(x.value),
                    value: formData.meta_data?.meta_data_type === MetaDataTypeEnum.checkbox ? Number(formData.value) : String(formData.value),
                };
            }
            return x;
        }) : selectedDocument.detailFieldValues.push({
            id: 0,
            document_id: selectedDocument.template.id,
            metadata_id: Number(formData.selected_field_type_id),
            old_value: formData.meta_data?.meta_data_type === MetaDataTypeEnum.checkbox ? Number(formData.value) : String(formData.value),
            value: formData.meta_data?.meta_data_type === MetaDataTypeEnum.checkbox ? Number(formData.value) : String(formData.value),
            metadata: formData.meta_data,
            created_at: '',
            updated_at: ''
        }) as unknown as IDetailFieldValuesDTO[],
    }
}



function removeTag(tags: Tag[], removingTag: Tag) {
    return tags.filter(tag => tag.value !== removingTag.value);
}


/**
 * Adds a new advanced filter to the list of existing filters.
 *
 * If there are already filters present, this function inserts a condition filter
 * (with type 'and') before adding the new filter. Each new filter is assigned a unique UUID.
 *
 * @param newFilter - The new filter to add, excluding the UUID property.
 * @param filters - The current list of advanced filters.
 * @returns A new array of advanced filters including the new filter (and a condition filter if applicable).
 */
function addAdvanceFilter(newFilter: Omit<IAdvanceFilter<AdvanceFilterParamType>, 'uuid'>, filters: IAdvanceFilter<AdvanceFilterParamType>[]): IAdvanceFilter<AdvanceFilterParamType>[] {
    if (filters?.length > 0) {
        return [
            ...filters,
            {
                uuid: createUniqueUUID(filters.map(x => x.uuid)),
                condition_type: 'and',
                field_type: AdvancedFilterFieldTypesEnum.condition,
                isApplied: true,
                isOpened: false,
                item: <AdvanceFilterParamType>{},
                text: ''
            } as IAdvanceFilter<AdvanceFilterParamType>,
            {
                ...newFilter,
                uuid: createUniqueUUID(filters.map(x => x.uuid)),
            } as IAdvanceFilter<AdvanceFilterParamType>,
        ]
    }
    return [
        {
            ...newFilter,
            uuid: createUniqueUUID(filters.map(x => x.uuid)),
        }
    ];
}


function updateAdvanceFilter(filterToUpdate: IAdvanceFilter<AdvanceFilterParamType>, filters: IAdvanceFilter<AdvanceFilterParamType>[]): IAdvanceFilter<AdvanceFilterParamType>[] {
    const tmp = [...filters];
    const itemIndex = tmp?.findIndex(x => x.uuid === filterToUpdate.uuid);

    if (itemIndex > -1) {
        tmp[itemIndex] = {
            ...tmp[itemIndex],
            ...filterToUpdate
        }
    }
    return tmp;
}

function removeAdvanceFilter(filterToRemove: IAdvanceFilter<AdvanceFilterParamType>, filters: IAdvanceFilter<AdvanceFilterParamType>[]): IAdvanceFilter<AdvanceFilterParamType>[] {
    const tmp = [...filters];
    const index = tmp.findIndex(x => x.uuid === filterToRemove.uuid);
    if (index > -1) {
        if ((tmp.length >= index + 1) && tmp[index]?.field_type == "condition") {
            // removing filter clicking on predicate
            tmp.splice(index, 2);
        } else if ((index - 1 > -1) && tmp[index]?.field_type != "condition") {
            // removing filter clicking on filter
            tmp.splice(index - 1, 2);
        } else if (index === 0 && tmp.length > 1 && tmp[index + 1].field_type == "condition") {
            //removing first item when we have more of them
            tmp.splice(index, 2);
        } else {
            // removing particular element
            tmp.splice(index, 1);
        }
    }
    return tmp;
}

function createUniqueUUID(list: string[]): string {
    const uuid = uuidGenerator.sequentialUUID();
    const contains = list.findIndex(x => x === uuid) > -1;
    if (contains) {
        return createUniqueUUID(list);
    }

    return uuid;
}

function onFilterClose(filter: Partial<IAdvanceFilter<AdvanceFilterParamType>>, filters: IAdvanceFilter<AdvanceFilterParamType>[]): IAdvanceFilter<AdvanceFilterParamType>[] {
    const tmp = [...filters];
    const itemIndex = tmp?.findIndex(x => x.uuid === filter.uuid);

    if (itemIndex > -1) {
        tmp[itemIndex] = {
            ...tmp[itemIndex],
            isOpened: false
        }
    }
    return tmp;
}

function areAllInternalRemindersPassed(reminders: DocumentInternalReminder[] = []): boolean {
    const validReminders = (reminders || []).filter(reminder => !!reminder?.reminder_datetime);
    if (!validReminders.length) {
        return false;
    }

    return validReminders.every(reminder => new Date(reminder.reminder_datetime).getTime() < Date.now());
}
